import { supabase } from "@/utils/supabase/client";
import { isAdmin, isSuperAdmin } from "@/utils/authHelpers";

/**
 * Gathers comprehensive user metadata for analytics tracking
 * @param {Object} session - The user session object
 * @returns {Object} User metadata object
 */
export const getUserMetadata = async (session) => {
  if (!session?.user?.id) {
    return {
      isAuthenticated: false,
      userId: null,
      email: null,
      userRole: null,
      userType: null,
      spaceIds: [],
      spaceCount: 0,
      publishedSpaceCount: 0,
      subscriptionTier: null,
      subscriptionStatus: null,
      hasUsedTrial: false,
      createdAt: null,
      lastSignInAt: null,
    };
  }

  try {
    // Get user settings and billing info
    const [userSettingsResponse, userBillingResponse, userSpacesResponse] =
      await Promise.all([
        supabase
          .from("user_settings")
          .select("user_role, user_type, created_at")
          .eq("id", session.user.id)
          .single(),
        supabase
          .from("user_billing")
          .select("subscription_tier, subscription_status, has_used_trial")
          .eq("id", session.user.id)
          .single(),
        supabase
          .from("spaces")
          .select("id, status, subscription_tier")
          .eq("user_id", session.user.id)
          .eq("is_deleted", false),
      ]);

    const userSettings = userSettingsResponse.data;
    const userBilling = userBillingResponse.data;
    const userSpaces = userSpacesResponse.data || [];

    // Calculate space metrics
    const spaceIds = userSpaces.map((space) => space.id);
    const publishedSpaces = userSpaces.filter(
      (space) => space.status === "published"
    );
    const lifetimeSpaces = userSpaces.filter(
      (space) => space.subscription_tier === "lifetime"
    );

    return {
      isAuthenticated: true,
      userId: session.user.id,
      email: session.user.email,
      userRole: userSettings?.user_role || "user",
      userType: userSettings?.user_type || "individual",
      spaceIds,
      spaceCount: userSpaces.length,
      publishedSpaceCount: publishedSpaces.length,
      lifetimeSpaceCount: lifetimeSpaces.length,
      subscriptionTier: userBilling?.subscription_tier || "free",
      subscriptionStatus: userBilling?.subscription_status || "inactive",
      hasUsedTrial: userBilling?.has_used_trial || false,
      createdAt: userSettings?.created_at || session.user.created_at,
      lastSignInAt: session.user.last_sign_in_at,
      // Additional session metadata
      emailConfirmed: session.user.email_confirmed_at ? true : false,
      phoneConfirmed: session.user.phone_confirmed_at ? true : false,
    };
  } catch (error) {
    console.error("Error gathering user metadata:", error);

    // Return basic metadata if detailed fetch fails
    return {
      isAuthenticated: true,
      userId: session.user.id,
      email: session.user.email,
      userRole: session.user.user_settings?.user_role || "user",
      userType: session.user.user_settings?.user_type || "individual",
      spaceIds: [],
      spaceCount: 0,
      publishedSpaceCount: 0,
      lifetimeSpaceCount: 0,
      subscriptionTier: "unknown",
      subscriptionStatus: "unknown",
      hasUsedTrial: false,
      createdAt: session.user.created_at,
      lastSignInAt: session.user.last_sign_in_at,
      emailConfirmed: session.user.email_confirmed_at ? true : false,
      phoneConfirmed: session.user.phone_confirmed_at ? true : false,
    };
  }
};

/**
 * Initializes Hotjar with user metadata for session identification
 * @param {Object} userMetadata - User metadata object from getUserMetadata
 */
export const initializeHotjarWithUserData = (userMetadata) => {
  if (typeof window === "undefined" || !window.hj) {
    return;
  }

  // Skip Hotjar on staging environment
  if (
    typeof window !== "undefined" &&
    window.location.hostname === "staging.qensa.me"
  ) {
    console.log("🚫 Skipping Hotjar identification on staging environment");
    return;
  }

  try {
    // Identify user in Hotjar
    if (userMetadata.isAuthenticated) {
      // Skip Hotjar for admin and super admin users
      if (
        userMetadata.userRole === "admin" ||
        userMetadata.userRole === "super_admin"
      ) {
        console.log("🚫 Skipping Hotjar identification for admin user:", {
          userId: userMetadata.userId?.slice(0, 8) + "...",
          userRole: userMetadata.userRole,
        });
        return;
      }

      window.hj("identify", userMetadata.userId, {
        email: userMetadata.email,
        user_role: userMetadata.userRole,
        user_type: userMetadata.userType,
        subscription_tier: userMetadata.subscriptionTier,
        subscription_status: userMetadata.subscriptionStatus,
        space_count: userMetadata.spaceCount,
        published_space_count: userMetadata.publishedSpaceCount,
        lifetime_space_count: userMetadata.lifetimeSpaceCount,
        has_used_trial: userMetadata.hasUsedTrial,
        created_at: userMetadata.createdAt,
        last_sign_in_at: userMetadata.lastSignInAt,
        email_confirmed: userMetadata.emailConfirmed,
        phone_confirmed: userMetadata.phoneConfirmed,
      });

      console.log("🔥 Hotjar user identified:", {
        userId: userMetadata.userId,
        email: userMetadata.email,
        userRole: userMetadata.userRole,
        spaceCount: userMetadata.spaceCount,
      });
    }
  } catch (error) {
    console.error("Error initializing Hotjar with user data:", error);
  }
};

/**
 * Enhanced Umami tracking with user metadata
 * @param {string} eventName - The event name to track
 * @param {Object} properties - Additional event properties
 * @param {Object} userMetadata - User metadata object from getUserMetadata
 */
export const trackUmamiEventWithUserData = (
  eventName,
  properties = {},
  userMetadata = null
) => {
  if (typeof window === "undefined" || !window.umami) {
    return;
  }

  // Skip Umami tracking on staging environment
  if (
    typeof window !== "undefined" &&
    window.location.hostname === "staging.qensa.me"
  ) {
    console.log(
      "🚫 Skipping Umami tracking on staging environment:",
      eventName
    );
    return;
  }

  try {
    // Skip Umami tracking for admin and super admin users
    if (userMetadata && userMetadata.isAuthenticated) {
      if (
        userMetadata.userRole === "admin" ||
        userMetadata.userRole === "super_admin"
      ) {
        console.log("🚫 Skipping Umami tracking for admin user:", {
          eventName,
          userId: userMetadata.userId?.slice(0, 8) + "...",
          userRole: userMetadata.userRole,
        });
        return;
      }
    }

    const eventData = {
      timestamp: new Date().toISOString(),
      ...properties,
    };

    // Add user metadata if provided and user is authenticated
    if (userMetadata && userMetadata.isAuthenticated) {
      eventData.user_id = userMetadata.userId;
      eventData.user_email = userMetadata.email;
      eventData.user_role = userMetadata.userRole;
      eventData.user_type = userMetadata.userType;
      eventData.subscription_tier = userMetadata.subscriptionTier;
      eventData.subscription_status = userMetadata.subscriptionStatus;
      eventData.space_count = userMetadata.spaceCount;
      eventData.published_space_count = userMetadata.publishedSpaceCount;
      eventData.lifetime_space_count = userMetadata.lifetimeSpaceCount;
      eventData.has_used_trial = userMetadata.hasUsedTrial;

      // Add space IDs if they exist and aren't too many (avoid URL length issues)
      if (
        userMetadata.spaceIds &&
        userMetadata.spaceIds.length > 0 &&
        userMetadata.spaceIds.length <= 10
      ) {
        eventData.space_ids = userMetadata.spaceIds.join(",");
      }
    }

    window.umami.track(eventName, eventData);

    console.log(`📊 Umami Analytics: ${eventName}`, eventData);
  } catch (error) {
    console.warn("Umami tracking error:", error);
  }
};

/**
 * Cache for user metadata to avoid repeated API calls
 */
let userMetadataCache = null;
let userMetadataCacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get cached user metadata or fetch fresh data
 * @param {Object} session - The user session object
 * @param {boolean} forceRefresh - Force refresh the cache
 * @returns {Object} User metadata object
 */
export const getCachedUserMetadata = async (session, forceRefresh = false) => {
  const now = Date.now();

  // Return cached data if valid and not forcing refresh
  if (
    !forceRefresh &&
    userMetadataCache &&
    userMetadataCacheTime &&
    now - userMetadataCacheTime < CACHE_DURATION &&
    userMetadataCache.userId === session?.user?.id
  ) {
    return userMetadataCache;
  }

  // Fetch fresh data
  const metadata = await getUserMetadata(session);

  // Cache the result
  userMetadataCache = metadata;
  userMetadataCacheTime = now;

  return metadata;
};

/**
 * Clear the user metadata cache (useful when user signs out or data changes)
 */
export const clearUserMetadataCache = () => {
  userMetadataCache = null;
  userMetadataCacheTime = null;
};
